/**
 * `GET /api/v1/me` — the no-hard-wired-people contract (ADR-001 §11.2).
 *
 * The SPA's navigation, its buttons and its home route are all rendered from this response.
 * `menu` is computed on the server from the caller's role (POLICY in @amplispot/domain) and mirrors the
 * prototype's left menu exactly: items, order, labels, icons, group headings and hub tabs.
 */
import { z } from 'zod';
import { IsoDate, Timestamp } from './common.js';
import { Capability } from './roles.js';
import { UserRef } from './users.js';
/** Icon names used by the prototype's `ico()` helper. The SPA maps each to its SVG. */
export const MenuIcon = z
    .enum(['alert', 'sun', 'spark', 'inbox', 'building', 'chart', 'users', 'rupee', 'gear', 'calendar'])
    .meta({ id: 'MenuIcon' });
/** Sidebar group heading printed *before* an item (prototype `NG`): Work, Business, Admin. */
export const MenuGroup = z.enum(['Work', 'Business', 'Admin']).meta({ id: 'MenuGroup' });
export const MenuBadge = z
    .object({
    count: z.number().int().positive(),
    /** `alert` renders red (prototype class `r`), `neutral` renders grey. */
    tone: z.enum(['alert', 'neutral']),
})
    .meta({ id: 'MenuBadge' });
export const MenuTab = z
    .object({
    /** Stable tab key — the prototype's tab id (e.g. `book`, `tsreport`, `uover`). */
    key: z.string().min(1),
    /** Label for this caller's role (members get "Plan and timesheet", "Routine", …). */
    label: z.string().min(1),
    /** SPA route, `/{item.key}/{tab.key}`. */
    route: z.string().startsWith('/'),
    /** `null` when there is nothing to count (no badge shown). */
    badge: MenuBadge.nullable(),
})
    .meta({ id: 'MenuTab' });
export const MenuKey = z
    .enum(['today', 'me', 'ideas', 'work', 'clients', 'usage', 'team', 'money', 'settings', 'my-requests', 'holidays'])
    .meta({ id: 'MenuKey' });
export const MenuItem = z
    .object({
    key: MenuKey,
    label: z.string().min(1),
    icon: MenuIcon,
    /** SPA route of the item; for a hub this opens its first tab. */
    route: z.string().startsWith('/'),
    /** Print this group heading above the item, or `null`. */
    group: MenuGroup.nullable(),
    /** `null` when there is nothing to count. Counts come from the step that owns the data. */
    badge: MenuBadge.nullable(),
    /**
     * Hub tabs in order. The sidebar shows a sub-menu only when there are 2 or more
     * (prototype `renderNav`); on mobile they render as a tab row. Empty for non-hub items.
     */
    tabs: z.array(MenuTab),
})
    .meta({ id: 'MenuItem' });
/** Timesheet gate (guide §2): a member must file the previous working day before the next opens. */
export const TimesheetGate = z
    .object({
    timesheet_blocked: z.boolean(),
    previous_working_day: IsoDate,
})
    .meta({ id: 'TimesheetGate' });
/** Product switches the SPA needs (all set in Settings). Additive — more keys arrive in later steps. */
export const MeFlags = z
    .object({
    /** Tone detection on client email (scope decision: ON by default, switchable in Settings). */
    tone_detection: z.boolean(),
})
    .meta({ id: 'MeFlags' });
/** Organisation settings every screen may need. Additive — more keys arrive in later steps. */
export const MeConfig = z
    .object({
    /** Expected hours per working day (Settings; default 7.5). Every timesheet calculation and label reads it. */
    working_day_hours: z.number().positive().max(24),
})
    .meta({ id: 'MeConfig' });
/**
 * Acting as (operator decision 18): the associate director may act as a member. While acting, `user`,
 * `capabilities`, `menu` and every data scope are the member's; `by` is the real person. The SPA shows a
 * banner while this is non-null. Every write made while acting is audited with both people.
 */
export const ActingAs = z
    .object({
    as: UserRef,
    by: UserRef,
    /** `act` = doing their work (writes recorded with both people); `view` = read-only, nothing can be changed. */
    mode: z.enum(['act', 'view']),
    /** 30-minute hard expiry; after it the session is the real user again. */
    expires_at: Timestamp,
    reason: z.string(),
})
    .meta({ id: 'ActingAs' });
export const MeResponse = z
    .object({
    /** The signed-in user. `email` is included because it is the caller's own. */
    user: UserRef.extend({ email: z.email() }),
    capabilities: z.array(Capability),
    menu: z.array(MenuItem),
    /** Where to land after sign-in: `/today` (managers), `/me` (members), `/my-requests` (requesters), `/holidays` (hr). */
    home_route: z.string().startsWith('/'),
    /**
     * `null` for roles the gate does not apply to (everyone but ops_member).
     * Timesheets ship in build step 4; until then the API returns `null` for every role.
     */
    gate: TimesheetGate.nullable(),
    flags: MeFlags,
    config: MeConfig,
    /** `null` unless the real user is acting as someone else. */
    acting_as: ActingAs.nullable(),
    /**
     * True while this session signed in with a temporary password (a manager's reset or the founder's bootstrap):
     * show the change-password screen. Until POST /auth/password/change succeeds, every other session route answers
     * 403 must_change_password (this one and POST /auth/logout still work).
     */
    must_change_password: z.boolean(),
})
    .meta({ id: 'MeResponse' });
//# sourceMappingURL=me.js.map